import { Flex, Text } from '@chakra-ui/layout';
import { useMemo, useState } from 'react';
import { Crucible, ERC20Token } from 'types';
import { useTransactions } from 'store/transactions/useTransactions';
import { useSelectedAssets } from 'store/selectedAssets';
import { useTokenBlacklist } from 'hooks/query/useTokenBlacklist';
import AssetsLoadingSkeleton from 'components/crucible/detail/assetManagement/AssetsLoadingSkeleton';
import AssetSelector from './assetSelector';
import transferErc20 from './transferErc20';
import TokenTransferForm from './tokenTransferForm';

// TODO: Update copy when readonly
type Props = {
  crucible: Crucible;
  crucibleAssets: ERC20Token[] | undefined;
  assetsStatus: 'idle' | 'error' | 'loading' | 'success';
  assetsFetching: boolean;
};

const WithdrawFromCrucible: React.FC<Props> = ({
  crucible,
  crucibleAssets,
  assetsStatus,
  assetsFetching
}) => {
  const { withdrawFromCrucible } = useTransactions();
  const { data: tokenBlacklist = [] } = useTokenBlacklist();
  const { selectedCrucibleAsset, setSelectedCrucibleAsset } = useSelectedAssets(
    crucible?.id
  );

  const [amount, setAmount] = useState('');

  const assetSelector = useMemo(() => {
    return (
      crucibleAssets && (
        <AssetSelector
          selectedAsset={selectedCrucibleAsset}
          assets={crucibleAssets}
          setSelectedAsset={setSelectedCrucibleAsset}
          tokenBlacklist={tokenBlacklist}
        />
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCrucibleAsset, crucibleAssets]);

  if (assetsStatus === 'error') {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700'>
        <Text textAlign='center'>
          Failed to get assets in your Crucible. Please try refreshing the page.
        </Text>
      </Flex>
    );
  }

  if (assetsStatus === 'loading' || assetsStatus === 'idle' || assetsFetching) {
    return <AssetsLoadingSkeleton />;
  }

  if (!selectedCrucibleAsset || !crucible || !crucible.id || !crucibleAssets) {
    return (
      <Flex p={[4, 6]} borderRadius='xl' bg='gray.700' direction='column'>
        <Text textAlign='center'>
          You have no unlocked assets available to withdraw from your Crucible.
          You can view a list of all of your assets in the "Summary" tab.
        </Text>
      </Flex>
    );
  }

  const tokenBalance = selectedCrucibleAsset?.value.amount;
  const tokenDecimals = selectedCrucibleAsset?.decimals || 18;

  const handleTransfer = () => {
    transferErc20(
      tokenBlacklist,
      selectedCrucibleAsset,
      withdrawFromCrucible,
      crucible.id,
      amount,
      tokenDecimals
    );
    setAmount('');
  };

  return (
    <TokenTransferForm
      tokenBalance={tokenBalance}
      tokenDecimals={tokenDecimals}
      selectedAsset={selectedCrucibleAsset}
      amount={amount}
      setAmount={setAmount}
      assetSelector={assetSelector}
      handleTransfer={handleTransfer}
      submitButtonText={'Withdraw from Crucible'}
    />
  );
};

export default WithdrawFromCrucible;
